import { useEffect, useSyncExternalStore } from 'react';

/**
 * One reference-counted body scroll lock for every overlay. Each overlay used to
 * save/restore `document.body.style.overflow` on its own, so nested overlays
 * (gallery → team overlay → member glimpse) raced each other on unmount order —
 * whichever cleanup ran last decided the page's scroll state.
 *
 * The FIRST acquire records the page's real prior overflow value and the LAST
 * release restores exactly that value (a bare `''` reset would erase a
 * pre-existing inline overflow).
 */
const LOCKED_BODY_OVERFLOW = 'hidden';

let bodyScrollLockCount = 0;
let previousBodyOverflow = '';
const bodyScrollLockListeners = new Set<() => void>();

function emitBodyScrollLockChange(): void {
  bodyScrollLockListeners.forEach(listener => listener());
}

function acquireBodyScrollLock(): void {
  bodyScrollLockCount += 1;
  if (bodyScrollLockCount === 1) {
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = LOCKED_BODY_OVERFLOW;
  }

  emitBodyScrollLockChange();
}

function releaseBodyScrollLock(): void {
  bodyScrollLockCount = Math.max(0, bodyScrollLockCount - 1);
  if (bodyScrollLockCount === 0) {
    document.body.style.overflow = previousBodyOverflow;
  }

  emitBodyScrollLockChange();
}

function subscribeToBodyScrollLock(listener: () => void): () => void {
  bodyScrollLockListeners.add(listener);
  return (): void => {
    bodyScrollLockListeners.delete(listener);
  };
}

function getBodyScrollLockSnapshot(): boolean {
  return bodyScrollLockCount > 0;
}

function getBodyScrollLockServerSnapshot(): boolean {
  return false;
}

/**
 * Read the shared ref-counted lock as state. Coverage owners (the landing Prism)
 * use it to learn that some overlay is on top of the page without knowing which.
 */
export function useBodyScrollLocked(): boolean {
  return useSyncExternalStore(
    subscribeToBodyScrollLock,
    getBodyScrollLockSnapshot,
    getBodyScrollLockServerSnapshot,
  );
}

/** Lock body scrolling while `active`; the lock is shared across all overlays. */
export function useBodyScrollLock({ active }: { active: boolean }): void {
  useEffect(() => {
    if (!active) return;
    acquireBodyScrollLock();
    return releaseBodyScrollLock;
  }, [active]);
}
